import { ImgHTMLAttributes } from "react";
import { useImageVariants, pickVariants } from "@/lib/image-variants";
import { Picture } from "./Picture";

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
  wrapperClassName?: string;
};

/**
 * <SmartImage> — drop-in <img> replacement that automatically serves
 * AVIF → WebP → original JPEG/PNG based on the visitor's browser support.
 *
 * Variants come from the public `image_variants` table (populated by the
 * Admin › Image Optimization backfill and by client-side upload transcoding).
 * If no variant exists for the given src, it renders a plain <img>.
 */
export function SmartImage({ src, wrapperClassName, className, ...rest }: Props) {
  const { data: map } = useImageVariants();
  const { avif, webp } = pickVariants(map, src);

  if (!avif && !webp) {
    // No variants — fall back to a plain <img> so we don't pay for <picture> overhead.
    // eslint-disable-next-line jsx-a11y/alt-text
    return <img src={src} className={className} loading="lazy" decoding="async" {...rest} />;
  }

  return (
    <Picture
      src={src}
      avif={avif ?? undefined}
      webp={webp ?? undefined}
      className={className}
      wrapperClassName={wrapperClassName}
      {...rest}
    />
  );
}

export default SmartImage;
